import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Pencil } from "lucide-react";
import {
  addCustomSection,
  addField,
  deleteField,
  publishForm,
  renameSection,
  saveDraft,
  updateField,
  updateForm,
} from "../../../../store/formsSlice";
import CustomizationPalette from "./CustomizationPalette";
import FormSectionCard from "./FormSectionCard";
import PreviewModal from "./PreviewModal";

export default function FormBuilder({
  form,
  onBack,
  onPublished,
  mode = "create",
  onSend,
}) {
  const dispatch = useDispatch();
  const [selectedSectionId, setSelectedSectionId] = useState(
    form.sections[0]?.id || null
  );
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [title, setTitle] = useState(form.name);

  const isRequest = mode === "request";
  const allowExtraSections =
    form.type === "pre-registration" || form.type === "post-registration" || isRequest;

  const handleSaveDraft = () => {
    dispatch(saveDraft(form.id));
    setDraftSaved(true);
    setTimeout(() => setDraftSaved(false), 2000);
  };

  const handlePublish = () => {
    dispatch(publishForm(form.id));
    onPublished?.(form.id);
  };

  const handlePaletteSelect = (type) => {
    if (type === "h1") {
      if (!allowExtraSections) return;
      const action = dispatch(addCustomSection({ formId: form.id }));
      setSelectedSectionId(action.payload.section.id);
      setEditingFieldId(null);
      return;
    }
    if (!selectedSectionId) return;
    dispatch(
      addField({
        formId: form.id,
        sectionId: selectedSectionId,
        type,
      })
    );
  };

  const commitTitle = () => {
    const next = title.trim() || form.name;
    dispatch(updateForm({ id: form.id, changes: { name: next } }));
    setTitle(next);
    setEditingTitle(false);
  };

  return (
    <div className="px-6 py-6 text-left">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="mb-3 text-sm text-gray-400 hover:text-gray-700"
            >
              {isRequest ? "← Back" : "← Back to drafts"}
            </button>
          )}
          {editingTitle ? (
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={commitTitle}
              onKeyDown={(e) => e.key === "Enter" && commitTitle()}
              className="border-b border-gray-300 text-2xl font-semibold text-[#1D2739] outline-none"
            />
          ) : (
            <h2 className="flex items-center gap-2 text-2xl font-semibold text-[#1D2739]">
              {form.name}
              <button
                type="button"
                onClick={() => setEditingTitle(true)}
                className="text-gray-400 hover:text-gray-700"
              >
                <Pencil className="h-4 w-4" />
              </button>
            </h2>
          )}
          <div className="mt-2 flex items-center gap-2">
            <span className="rounded-full bg-gray-200 px-3 py-1 text-xs font-medium text-gray-700">
              {isRequest ? "Pre-Registration Form" : form.typeLabel}
            </span>
            <span className="rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-500">
              Draft
            </span>
            {draftSaved && (
              <span className="text-xs font-medium text-green-600">Draft saved</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSaveDraft}
            className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700"
          >
            Save Draft
          </button>
          <button
            type="button"
            onClick={() => setShowPreview(true)}
            className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700"
          >
            Preview
          </button>
          {isRequest ? (
            <button
              type="button"
              onClick={onSend}
              className="rounded-lg bg-[#EAF524] px-4 py-2 text-sm font-semibold text-black"
            >
              Send
            </button>
          ) : (
            <button
              type="button"
              onClick={handlePublish}
              className="rounded-lg bg-[#EAF524] px-4 py-2 text-sm font-semibold text-black"
            >
              Publish
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-8">
        <CustomizationPalette
          disabledTypes={[
            ...(!allowExtraSections ? ["h1"] : []),
            ...(!selectedSectionId ? ["h2", "body", "text", "dropdown", "checkbox", "logo", "file"] : []),
          ]}
          onSelect={handlePaletteSelect}
        />
        <div className="min-w-0 flex-1">
          <p className="mb-3 text-sm text-gray-400">Click on a section to edit</p>
          {form.sections.map((section) => (
            <FormSectionCard
              key={section.id}
              section={section}
              selected={selectedSectionId === section.id}
              onSelect={() => setSelectedSectionId(section.id)}
              editingFieldId={editingFieldId}
              setEditingFieldId={setEditingFieldId}
              onRename={(next) =>
                dispatch(
                  renameSection({
                    formId: form.id,
                    sectionId: section.id,
                    title: next,
                  })
                )
              }
              onEditField={(fieldId, changes) =>
                dispatch(
                  updateField({
                    formId: form.id,
                    sectionId: section.id,
                    fieldId,
                    changes,
                  })
                )
              }
              onDeleteField={(fieldId) =>
                dispatch(
                  deleteField({
                    formId: form.id,
                    sectionId: section.id,
                    fieldId,
                  })
                )
              }
            />
          ))}
        </div>
      </div>

      <PreviewModal
        open={showPreview}
        form={form}
        onClose={() => setShowPreview(false)}
      />
    </div>
  );
}
